import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import './PracticeExam.css';
import CircularTimer from './CircularTimer';
import studentApi from './StudentApi';
import { FiEdit, FiArrowLeft, FiArrowRight, FiSend, FiBookmark } from 'react-icons/fi';
import Swal from 'sweetalert2';

const DURATION_MINUTES = 60;

function PracticeExam() {
    const navigate = useNavigate();
    const location = useLocation();

    // Hooks must be at top
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(DURATION_MINUTES * 60);
    const [markedForReview, setMarkedForReview] = useState({});
    const [showTimeWarning, setShowTimeWarning] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const TOTAL_QUESTIONS = questions.length;

    // Extract state from navigation
    let { examId, subjectId, topicId, questions: passedQuestions,  practiceSetId, mode } = location.state || {};

    // Ref for examId to avoid missing ID
    const examIdRef = useRef(examId);

    // Fetch questions or use passedQuestions
   useEffect(() => {
    if (passedQuestions && passedQuestions.length > 0) {
        setQuestions(passedQuestions);
        examIdRef.current = examIdRef.current || passedQuestions[0].exam_id;
        setLoading(false);
        return;
    }

    if (!examIdRef.current && !topicId && !subjectId && !practiceSetId) {
        setError('Missing exam, topic, or practice set information.');
        setLoading(false);
        return;
    }

    // Construct query
    let query = '';
    if (practiceSetId) query = `practice_set_id=${practiceSetId}`;
    else if (topicId) query = `topic_id=${topicId}`;
    else if (subjectId) query = `subject_id=${subjectId}`;
    else if (examIdRef.current) query = `exam_id=${examIdRef.current}`;

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const res = await studentApi.get(`/questions?${query}`);
            const formatted = res.data.map(q => ({
                id: q.id,
                exam_id: q.exam_id,
                question: q.question_text,
                options: [q.option_a, q.option_b, q.option_c, q.option_d],
                correct_option_array: q.correct_option_array,
                type: q.question_type === 'MCQ_RADIO' ? 'single' : 'multiple'
            }));
            setQuestions(formatted);
            if (!examIdRef.current && formatted.length > 0) {
                examIdRef.current = formatted[0].exam_id;
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load questions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    fetchQuestions();
}, [practiceSetId, topicId, subjectId, passedQuestions]);

    // Timer
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    if (!isSubmitting) submitExamNow();
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [questions, isSubmitting]);

    useEffect(() => {
        if (timeLeft === 600 && !showTimeWarning) setShowTimeWarning(true);
    }, [timeLeft, showTimeWarning]);

    // LocalStorage key
    const storageKey = `practice-progress-${examIdRef.current || questions[0]?.exam_id}-${topicId}`;

    // Save progress
    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify({ selectedAnswers, timeLeft }));
    }, [selectedAnswers, timeLeft, storageKey]);

    // Load progress
    useEffect(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const { selectedAnswers, timeLeft } = JSON.parse(saved);
            setSelectedAnswers(selectedAnswers || {});
            setTimeLeft(timeLeft || DURATION_MINUTES * 60);
        }
    }, [storageKey]);

    // Submit exam
    const submitExamNow = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const studentData = JSON.parse(localStorage.getItem('studentData') || '{}');
            const studentId = parseInt(studentData.id, 10);

            if (!studentId) {
                Swal.fire('Error', 'Student ID missing. Please login again.', 'error');
                navigate('/student-login');
                setIsSubmitting(false);
                return;
            }

            if (!examIdRef.current) {
                Swal.fire('Error', 'Exam ID missing. Cannot submit exam.', 'error');
                setIsSubmitting(false);
                return;
            }

            const answersPayload = questions.map((q, idx) => {
                const sel = selectedAnswers[idx];
                return q.type === 'single'
                    ? { question_id: q.id, selected_option: sel || null, selected_options: [] }
                    : { question_id: q.id, selected_option: null, selected_options: Array.isArray(sel) ? sel.map(String) : [] };
            });

            const payload = {
                student_id: studentId,
                exam_id: examIdRef.current,
                source_type: 'practice',
                answers: answersPayload
            };

            const res = await studentApi.post('/exam/submit', payload);

            Swal.fire({
                icon: 'success',
                title: 'Exam Submitted!',
                text: `Total Marks: ${res.data.total_marks}, Correct: ${res.data.correct}, Wrong: ${res.data.wrong}`,
            }).then(() => {
                navigate('/result', { state: { ...res.data, selectedAnswers, questions, examId: examIdRef.current, topicId, subjectId } });
            });

        } catch (error) {
            console.error('Error submitting exam:', error.response || error.message);
            Swal.fire({
                icon: 'error',
                title: 'Failed to submit exam',
                text: error.response?.data?.message || 'Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render
    return (
        <MathJaxContext>
            <div className="practice-exam-container">
                {loading ? (
                    <p>Loading questions...</p>
                ) : error ? (
                    <p style={{ color: 'red' }}>{error}</p>
                ) : questions.length === 0 ? (
                    <p>No questions found for this exam.</p>
                ) : (
                    <div className="exam-layout">
                        <div className="exam-main">
                            <div className="exam-header">
                                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <FiEdit size={24} color='#fd7e14' /> Practice Exam
                                </h2>
                                <CircularTimer duration={DURATION_MINUTES * 60} timeLeft={timeLeft} />
                            </div>

                            <div className="question-box">
                                <div className="question-content">
                                    <h3>Q{currentIndex + 1}:</h3>
                                    <MathJax>
                                        <div dangerouslySetInnerHTML={{ __html: questions[currentIndex]?.question || '' }} />
                                    </MathJax>
                                </div>

                                <div className="options">
                                    {questions[currentIndex]?.options?.map((opt, idx) => {
                                        const type = questions[currentIndex]?.type;
                                        const name = `question-${currentIndex}`;
                                        const optionLetter = String.fromCharCode(65 + idx);
                                        const isSelected =
                                            type === "single"
                                                ? selectedAnswers[currentIndex] === optionLetter
                                                : selectedAnswers[currentIndex]?.includes(optionLetter);

                                        const handleChange = e => {
                                            if (type === 'single') {
                                                setSelectedAnswers({ ...selectedAnswers, [currentIndex]: e.target.value });
                                            } else {
                                                const existing = selectedAnswers[currentIndex] || [];
                                                setSelectedAnswers({
                                                    ...selectedAnswers,
                                                    [currentIndex]: e.target.checked
                                                        ? [...existing, e.target.value]
                                                        : existing.filter(item => item !== e.target.value)
                                                });
                                            }
                                        };

                                        return (
                                            <label key={idx} className={`option-button ${isSelected ? 'selected' : ''}`}>
                                                <input
                                                    type={type === 'single' ? 'radio' : 'checkbox'}
                                                    name={name}
                                                    value={optionLetter}
                                                    checked={isSelected}
                                                    onChange={handleChange}
                                                    className="visible-input"
                                                />
                                                <MathJax>
                                                    <span>
                                                        <strong>{optionLetter}.</strong>{' '}
                                                        <span dangerouslySetInnerHTML={{ __html: opt }} />
                                                    </span>
                                                </MathJax>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="navigation-buttons">
                                <button onClick={() => setCurrentIndex(i => i - 1)} disabled={currentIndex === 0}>
                                    <FiArrowLeft /> Previous
                                </button>
                                <button onClick={() => setCurrentIndex(i => i + 1)} disabled={currentIndex === TOTAL_QUESTIONS - 1}>
                                    Next <FiArrowRight />
                                </button>
                                <button
                                    onClick={() => setMarkedForReview(prev => ({ ...prev, [currentIndex]: !prev[currentIndex] }))}
                                    className={`marked-review-button ${markedForReview[currentIndex] ? 'marked' : ''}`}
                                >
                                    <FiBookmark /> {markedForReview[currentIndex] ? 'Unmark Review' : 'Mark for Review'}
                                </button>
                                <button onClick={submitExamNow} className='submit-button' disabled={isSubmitting}>
                                    <FiSend /> Submit
                                </button>
                            </div>

                            {/* Progress */}
                            <div className="progress-chart">
                                <div className="bar-container">
                                    <div
                                        className="bar-fill-attempted"
                                        style={{ width: `${(Object.keys(selectedAnswers).length / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                    <div
                                        className="bar-fill-unattempted"
                                        style={{ width: `${((TOTAL_QUESTIONS - Object.keys(selectedAnswers).length) / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                </div>
                            </div>
                            <div className="progress-labels">
                                <span>✅ Attempted: {Object.keys(selectedAnswers).length}</span>
                                <span>❌ Unattempted: {TOTAL_QUESTIONS - Object.keys(selectedAnswers).length}</span>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="exam-sidebar">
                            <div className="tracker-legend">
                                <div className="legend-item"><div className="legend-box answered"></div> Answered</div>
                                <div className="legend-item"><div className="legend-box unanswered"></div> Not Answered</div>
                                <div className="legend-item"><div className="legend-box not-visited"></div> Not Visited</div>
                                <div className="legend-item"><div className="legend-box review"></div> Mark for Review</div>
                            </div>

                            <div className="question-tracker">
                                {questions.map((_, i) => {
                                    const isCurrent = currentIndex === i;
                                    const hasAnswered = selectedAnswers[i];
                                    const wasVisited = i <= currentIndex;
                                    const isReviewMarked = markedForReview[i];
                                    const status = isReviewMarked ? 'review' : hasAnswered ? 'answered' : wasVisited ? 'unanswered' : 'not-visited';
                                    return (
                                        <div
                                            key={i}
                                            className={`tracker-box ${status} ${isCurrent ? 'current' : ''}`}
                                            onClick={() => setCurrentIndex(i)}
                                            title={`Q${i + 1} — ${status.replace('-', ' ')}`}
                                        >
                                            {i + 1}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MathJaxContext>
    );
}

export default PracticeExam;
